import { createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { RouterErrorFallback } from '@/components/errorfallback/app-error-fallback'

// Next.js always exposes query params as strings ("0" is truthy, 0 is not).
// TanStack Router's default parseSearch applies JSON.parse to each value, so
// numeric strings like "0" become the number 0 (falsy), breaking every page
// that does `if (moduleCode)` or `moduleCode ? ... : ...`.
// Using URLSearchParams keeps every value as a string, matching Next.js behaviour.
const parseSearch = (searchStr: string): Record<string, string | string[]> => {
    const params = new URLSearchParams(searchStr)
    const result: Record<string, string | string[]> = {}
    for (const key of new Set(params.keys())) {
        const values = params.getAll(key)
        result[key] = values.length === 1 ? values[0] : values
    }
    return result
}

const stringifySearch = (search: Record<string, any>): string => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(search)) {
        if (v === undefined || v === null) continue
        if (Array.isArray(v)) {
            v.forEach((val: any) => params.append(k, String(val)))
        } else {
            params.set(k, String(v))
        }
    }
    const qs = params.toString()
    return qs ? `?${qs}` : ''
}

// defaultErrorComponent replaces the error boundary the deleted pages/_app.tsx used to
// provide: route render errors are caught by the router's own boundary (they never reach
// an outer React error boundary), reported to Sentry, and shown the branded fallback.
export const router = createRouter({
    routeTree,
    parseSearch,
    stringifySearch,
    defaultErrorComponent: RouterErrorFallback,
})

declare module '@tanstack/react-router' {
    interface Register { router: typeof router }
}
